/**
 * Componente Main para LocationOrders
 *
 * Responsabilidades:
 * - Orquestar la vista (desktop/mobile)
 * - Controlar LoadingState inicial con isInitialLoad
 * - Aplicar opacidad durante refetch (NO durante infinite scroll)
 * - Mostrar EmptyView según hasActiveFilters
 * - Mantener AG Grid siempre montado
 */
import { TotalResults } from '@shared/components/ui/TotalResults';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useLocationOrdersMain } from '../../hooks/useLocationOrdersMain';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { CardList, LocationOrdersAgGridTable } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection } from '../interaction';

export const Main = () => {
  const { t } = useTranslation('locationordersModule');

  // 🎯 Hook para manejar query parameters (para URL sync)
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
  });

  // 🎯 Estados locales sincronizados con URL
  const [searchTerm, setSearchTerm] = useState(queryParams.search ?? '');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? queryParams.filters : [],
  );

  // 🎯 Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 🎯 Sincronizar estados locales con cambios en URL
  useEffect(() => {
    setSearchTerm(queryParams.search ?? '');
  }, [queryParams.search]);

  useEffect(() => {
    setFilterConditions(Array.isArray(queryParams.filters) ? queryParams.filters : []);
  }, [queryParams.filters]);

  // 🎯 Handlers para sincronizar con URL - memoizados para evitar bucles
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      // Solo actualizar URL si el valor es diferente al actual
      if (queryParams.search !== searchValue) {
        updateQueryParams({ search: searchValue });
      }
    },
    [updateQueryParams, queryParams.search],
  );

  const handleFilterChangeWrapper = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      // Solo actualizar URL si los filtros son diferentes
      const currentFilters = Array.isArray(queryParams.filters) ? queryParams.filters : [];
      if (JSON.stringify(currentFilters) !== JSON.stringify(conditions)) {
        updateQueryParams({ filters: conditions });
      }
    },
    [updateQueryParams, queryParams.filters],
  );

  // 🎯 Hook que maneja toda la lógica de negocio
  const {
    // Estados
    selectedColumns,

    // Datos
    locationorders,
    isLoading,
    isFetching,
    error,
    total,

    // Props para componentes
    locationordersViewProps,

    // Handlers
    handleColumnsChange,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Estado de filtros
    hasActiveFilters,
  } = useLocationOrdersMain(searchTerm, filterConditions);

  // 🎯 Cada vez que se monta la vista listado (p. ej. al volver desde detalle), forzar refetch: la query tiene staleTime y el caché seguía mostrando el estado anterior
  useEffect(() => {
    void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intencional: solo al montar Main (nueva visita a /location-orders)
  }, []);

  // 🎯 Obtener columnas filtradas (todas siempre visibles)
  const { columnDefs, tanStackColumns } = useAgGridColumns(selectedColumns);

  // 🎯 Columnas para el selector: usar las columnas de AG Grid convertidas a TanStack
  const selectorColumns = useMemo(() => tanStackColumns, [tanStackColumns]);

  // 🎯 Efecto para desactivar el spinner de carga inicial después del primer fetch
  useEffect(() => {
    // Si isLoading pasa de true a false, significa que la carga inicial terminó.
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // 🎯 Función que decide qué vista renderizar (Desktop vs Mobile)
  const renderLocationOrders = () => {
    if (isMobile) {
      return <CardList {...locationordersViewProps} showActions={true} selectedColumns={selectedColumns} />;
    }

    // 🎯 Usar AG Grid para la vista de escritorio
    return (
      <LocationOrdersAgGridTable
        {...locationordersViewProps}
        columnDefs={columnDefs} // 🎯 Pasar columnas (todas visibles)
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    );
  };

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad) {
    return <LoadingState message={t('loading.message', 'Cargando órdenes de ubicación...')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      {/* Header con filtros y total */}
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection handleSearch={handleSearch} onFilterChange={handleFilterChangeWrapper} />
            <ColumnSelector
              columns={selectorColumns}
              selected={selectedColumns}
              onColumnsChange={handleColumnsChange}
              fixedColumns={['id']}
              storageKey="location-orders-ag-grid-table"
            />
          </div>
          <TotalResults total={total} />
        </div>
      </div>

      {/* Estado de carga - NO se muestra porque usamos isInitialLoad */}
      {/* {isLoading && <LoadingState message={t('loading.message')} />} */}

      {/* Tabla SIEMPRE montada - Solo cambia opacidad/altura */}
      <div
        className={`transition-opacity duration-300 mb-4 mt-4 ${
          isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'
        } ${!isLoading && locationorders.length === 0 ? 'h-0 overflow-hidden' : 'h-auto'}`}
      >
        {renderLocationOrders()}
      </div>

      {/* Estado vacío - Solo se muestra cuando no hay datos y no está cargando */}
      {!isLoading && locationorders.length === 0 && (
        <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.HANDS} />
      )}
    </div>
  );
};
